import { EnumObjects, makeStringDataField } from "@microsoft/oteljs";
import { OneDSSink } from "@microsoft/oteljs-1ds";
import { createOversizedEventHandler } from "./OversizedEventHandler";
/**
 * Create a OneDSSink
 * @param options Options object containing all relevant info needed
 * @param privacyGuard Privacy Guard instance to be used with the sink
 */ export function createOneDSSink(options, privacyGuard) {
    // Define core UTel values for the sink - user, app, session, and optionally release
    const app = {
        platform: options.appPlatform,
        name: options.appName,
        version: options.appVersion
    };
    const session = {
        id: options.sessionId
    };
    const user = options.user;
    const coreFields = {
        app: app,
        user: user,
        session: session,
        release: {
            audienceGroup: options.release?.audienceGroup
        }
    };
    const privacyGuardPlugin = privacyGuard.getPlugin();
    const privacyGuardConfig = privacyGuard.getConfiguration();
    // Any additional data fields that need to be sent with every event gets set here.
    // The names of these data fields don't automatically get prefixed with "Data."
    // Generally they should be Type A data fields as in http://aka.ms/officeeventschema.
    const persistentDataFields = options.persistentDataFields ?? [];
    persistentDataFields.push(makeStringDataField("Culture.UiLanguage", options.appLanguage));
    options.release?.audience && persistentDataFields.push(makeStringDataField("Release.Audience", options.release.audience));
    // Enable oversized event handling by default (can be disabled via options)
    const enableOversizedHandling = options.enableOversizedEventHandling !== false;
    // Use a deferred sink reference to handle the circular dependency:
    // The handler needs to send events through the sink, but the sink
    // needs the notification listener at construction time.
    // We solve this by using a mutable holder object that we update after sink creation.
    const sinkHolder = {
        sink: null
    };
    let notificationListener;
    if (enableOversizedHandling) {
        notificationListener = createOversizedEventHandler({
            sendSummaryEvent: (eventName, dataFields)=>{
                // Use the deferred sink reference
                if (sinkHolder.sink) {
                    const summaryEvent = {
                        eventName: `Office.ProjectPlanner.Planner.${eventName}`,
                        eventFlags: {
                            dataCategories: EnumObjects.DataCategories.ProductServiceUsage,
                            diagnosticLevel: EnumObjects.DiagnosticLevel.RequiredServiceData
                        },
                        dataFields: dataFields
                    };
                    sinkHolder.sink.sendTelemetryEvent(summaryEvent);
                }
            },
            traceLogger: options.traceLogger
        });
    }
    const oneDsSink = new OneDSSink(persistentDataFields, {
        endpointUrl: options.user.environment,
        coreFields: coreFields,
        extensionConfig: {
            [privacyGuardPlugin.identifier]: privacyGuardConfig
        },
        plugins: [
            privacyGuardPlugin
        ],
        notificationListener: notificationListener
    });
    // Init() validates the required core fields on the OneDSSink.
    // If the fields are invalid, the sink is disabled and should not be added.
    const success = oneDsSink.init();
    if (!success) {
        oneDsSink.shutdown();
        return null;
    }
    // Update the sink holder so the oversized event handler can access it
    sinkHolder.sink = oneDsSink;
    return oneDsSink;
}
